import { useQuery } from '@tanstack/react-query';
import {
    compareStrategicVolumeAndCapacityReport,
    getLastStrategicVolumeUploadDate,
    getStrategicVolumeReport,
    getStrategicVolumeReports,
    getStrategicVolumeReportSummary,
} from '../../services/strategicVolumeReport';
import { QueryKey } from '../../enums/QueryKey';
import {
    formatLastUploadDate,
    getDateRangeFromStartDateByAddingMonths,
    getStartAndEndOfAMonth,
} from '../../utils/dateUtils';
import { Dayjs } from '@utils/dayjs';
import { StrategicVolumeReport } from '../../types/StrategicVolumeReport';
import { DATE_FORMATS } from '@callmart/types';

export function getStrategicVolumeReportsQueryKey({
    date,
    location,
    user,
    releaseType,
    numOfMonthColumns,
}: {
    date: any;
    location: any;
    user: any;
    releaseType: any;
    numOfMonthColumns: any;
}) {
    const { dateFrom, dateTo } = getDateRangeFromStartDateByAddingMonths(date, numOfMonthColumns);

    return [
        QueryKey.StrategicVolumeReports,
        {
            dateFrom: dateFrom.format(DATE_FORMATS.ISO_DATETIME),
            dateTo: dateTo.format(DATE_FORMATS.ISO_DATETIME),
            location,
            user,
            releaseType,
        },
    ];
}

export function getStrategicVolumeAndCapacityReportComparisonQueryKey({
    date,
    location,
    user,
    releaseType,
    numOfMonthColumns,
}: {
    date: any;
    location: any;
    user: any;
    releaseType: any;
    numOfMonthColumns: any;
}) {
    const { dateFrom, dateTo } = getDateRangeFromStartDateByAddingMonths(date, numOfMonthColumns);
    return [
        QueryKey.StrategicVolumeAndCapacityReportComparison,
        {
            dateFrom: dateFrom.format(DATE_FORMATS.ISO_DATETIME),
            dateTo: dateTo.format(DATE_FORMATS.ISO_DATETIME),
            location,
            user,
            releaseType,
        },
    ];
}

export function useStrategicVolumeReports({
    date,
    location,
    user,
    releaseType,
    numOfMonthColumns,
}: {
    date: any;
    location: any;
    user: any;
    releaseType: any;
    numOfMonthColumns: any;
}) {
    const { dateFrom, dateTo } = getDateRangeFromStartDateByAddingMonths(date, numOfMonthColumns);
    const queryKey = getStrategicVolumeReportsQueryKey({ date, location, user, releaseType, numOfMonthColumns });

    const { data, refetch } = useQuery({
        queryKey,
        queryFn: () =>
            getStrategicVolumeReports({
                location,
                dateFrom: dateFrom.toISOString(),
                dateTo: dateTo.toISOString(),
                user,
                releaseType,
            }),
        enabled: !!date && !!user,
        initialData: [],
    });

    return { data, refetch };
}

export function useStrategicVolumeReportSummary({ date }: { date: Dayjs | string }) {
    const { dateFrom, dateTo }: { dateFrom: Dayjs; dateTo: Dayjs } = getStartAndEndOfAMonth(date);

    const { data, ...rest } = useQuery({
        queryKey: [
            QueryKey.StrategicVolumeReportsSummary,
            {
                dateFrom: dateFrom.format(DATE_FORMATS.ISO_DATETIME),
                dateTo: dateTo.format(DATE_FORMATS.ISO_DATETIME),
            },
        ],
        queryFn: () =>
            getStrategicVolumeReportSummary({
                dateFrom: dateFrom.toISOString(),
                dateTo: dateTo.toISOString(),
            }),
        enabled: !!date,
        refetchOnWindowFocus: false,
    });

    return { data: data || [], ...rest };
}

export function useLastStrategicVolumeReportUploadDate(location: any) {
    const { data } = useQuery({
        queryKey: [QueryKey.LastUploadDate + '-strategicVolumeReport', { location }],
        queryFn: () => getLastStrategicVolumeUploadDate(location),
        enabled: !!location,
    });

    return formatLastUploadDate(data);
}

export function useCompareStrategicVolumeAndCapacityReport({
    date,
    location,
    user,
    releaseType,
    numOfMonthColumns,
    showComparisonWithCapacityReport,
}: {
    date: Dayjs;
    location: any;
    user: any;
    releaseType: any;
    numOfMonthColumns: any;
    showComparisonWithCapacityReport: any;
}) {
    const { dateFrom, dateTo } = getDateRangeFromStartDateByAddingMonths(date, numOfMonthColumns);
    const queryKey = getStrategicVolumeAndCapacityReportComparisonQueryKey({
        date,
        location,
        user,
        releaseType,
        numOfMonthColumns,
    });

    const { data, refetch } = useQuery({
        queryKey,
        queryFn: () => compareStrategicVolumeAndCapacityReport(location, dateFrom, dateTo, user, releaseType),
        enabled: showComparisonWithCapacityReport,
        initialData: [],
    });

    return { data, refetch };
}

export function useStrategicVolumeReport({
    workingUnitLocation,
    date,
    enabled = true,
}: {
    workingUnitLocation: any;
    date: any;
    enabled?: boolean;
}): {
    strategicVolumeReport?: StrategicVolumeReport;
} {
    const queryKey = [QueryKey.StrategicVolumeReport, { workingUnitLocation, date }];

    const { data: strategicVolumeReport } = useQuery({
        queryKey,
        queryFn: () => getStrategicVolumeReport({ workingUnitLocation, date }),
        enabled: !!workingUnitLocation && !!date && enabled,
    });

    return { strategicVolumeReport };
}
